// 付箋・テキスト・図形ラベルなどの編集モードを担うフック。
//
// 振る舞い:
// - ダブルクリックで編集モードに遷移し、対象要素を contentEditable にする
// - IME（CompositionEvent）中は op を送らず、composition 確定後に変更を反映
// - キー入力に対しては 50ms debounce で object.text.update op を送信する
// - blur で編集モードを終了する
//
// 50ms debounce は同時編集体験を担保するためのバランス（README §11）。

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

interface Options {
  readonly objectId: string;
  readonly initialText: string;
  readonly elementRef: RefObject<HTMLElement | null>;
  readonly dispatcher: CanvasOpDispatcher;
  /** ロック中など編集を抑止したい場合に true を渡す。 */
  readonly disabled?: boolean | undefined;
}

interface Result {
  readonly isEditing: boolean;
  readonly enterEditMode: () => void;
  readonly leaveEditMode: () => void;
  readonly editorProps: {
    readonly contentEditable: boolean;
    readonly suppressContentEditableWarning: boolean;
    readonly onInput: (event: React.FormEvent<HTMLElement>) => void;
    readonly onCompositionStart: () => void;
    readonly onCompositionEnd: (event: React.CompositionEvent<HTMLElement>) => void;
    readonly onBlur: () => void;
    readonly onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  };
}

const DEBOUNCE_MS = 50;

export function useObjectTextEditor(options: Options): Result {
  const { objectId, initialText, elementRef, dispatcher, disabled } = options;
  const [isEditing, setEditing] = useState(false);
  const composingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 無効化された場合はその瞬間に編集モードを終了し、保留中の debounce timer も破棄する。
  // 他クライアントから locked=true が反映された直後に古い text を送信して
  // ロック後のオブジェクトを書き換えてしまうのを防ぐ（dispatcher 側にも防御を入れている）。
  useEffect(() => {
    if (!disabled) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (isEditing) {
      setEditing(false);
    }
  }, [disabled, isEditing]);

  const flushPending = useCallback(
    (text: string) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      dispatcher.updateText(objectId, text);
    },
    [dispatcher, objectId]
  );

  const scheduleSend = useCallback(
    (text: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        dispatcher.updateText(objectId, text);
      }, DEBOUNCE_MS);
    },
    [dispatcher, objectId]
  );

  // 編集モードに入った直後は要素にフォーカスし、初期テキストを反映する。
  //
  // 競合防止: editing 中に他者編集や自分の debounce echo で `initialText` が変動しても
  // ローカル入力を上書きしないよう、最新値は ref に保持して effect の依存からは外す。
  // 結果として「isEditing が false→true へ遷移した瞬間」だけ初期化され、編集中の
  // 外部更新は無視される（他者の最新値は editing 終了後の再レンダーで反映される）。
  const initialTextRef = useRef(initialText);
  initialTextRef.current = initialText;
  useEffect(() => {
    if (!isEditing) return;
    const el = elementRef.current;
    if (!el) return;
    const initial = initialTextRef.current;
    if (el.textContent !== initial) {
      el.textContent = initial;
    }
    el.focus();
    // キャレットを末尾に
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [isEditing, elementRef]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const enterEditMode = useCallback(() => {
    if (disabled) return;
    setEditing(true);
  }, [disabled]);
  const leaveEditMode = useCallback(() => {
    const el = elementRef.current;
    if (el) {
      flushPending(readEditorText(el));
    }
    setEditing(false);
  }, [elementRef, flushPending]);

  const onInput = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      if (composingRef.current) return;
      const text = readEditorText(event.currentTarget as HTMLElement);
      scheduleSend(text);
    },
    [scheduleSend]
  );

  const onCompositionStart = useCallback(() => {
    composingRef.current = true;
  }, []);

  const onCompositionEnd = useCallback(
    (event: React.CompositionEvent<HTMLElement>) => {
      composingRef.current = false;
      const text = readEditorText(event.currentTarget as HTMLElement);
      scheduleSend(text);
    },
    [scheduleSend]
  );

  const onBlur = useCallback(() => {
    leaveEditMode();
  }, [leaveEditMode]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        leaveEditMode();
      }
      // Enter キーはブラウザ既定の改行挿入に委ねる（自前で "\n" テキストノードを挿入すると、
      // 末尾改行がうまく表示・配置されない contentEditable 既知の問題に当たるため）。
      // 改行の取得は readEditorText（innerText 経由）で行うので、ブラウザが <div> / <br> を
      // 挿入しても改行は確実に object.text へ反映される（要望②の改行不具合の対策）。
    },
    [leaveEditMode]
  );

  return {
    isEditing,
    enterEditMode,
    leaveEditMode,
    editorProps: {
      contentEditable: isEditing,
      suppressContentEditableWarning: true,
      onInput,
      onCompositionStart,
      onCompositionEnd,
      onBlur,
      onKeyDown,
    },
  };
}

/**
 * contentEditable の表示テキストを改行付きで取得する。
 *
 * Enter キーでブラウザが挿入する <div> / <br> を textContent は改行へ変換せず連結するため、
 * textContent では改行が失われてしまう（要望②の改行が反映されない不具合の原因）。
 * innerText は「実際に表示されているテキスト」を返し、<div> / <br> 由来の改行を "\n" として
 * 含むため、white-space: pre-wrap の表示と一致した文字列（object.text へ保存する値）を得られる。
 * innerText 非対応の環境（jsdom 等のテスト環境）では textContent にフォールバックする。
 */
function readEditorText(el: HTMLElement): string {
  const rendered = (el as { innerText?: string | undefined }).innerText;
  return typeof rendered === "string" ? rendered : el.textContent ?? "";
}
